import { useEffect, useRef, useState } from 'react';
import type { MenuItemWithDetails } from '../../lib/twin';
import { formatPrice } from '../../lib/twin';

interface Props {
  onClose: () => void;
  featured: MenuItemWithDetails[];
}

interface Message {
  from: 'bot' | 'you';
  text: string;
}

const GREETING: Message = {
  from: 'bot',
  text: "Hi! I'm the Bean Counter barista bot. This is a demo, so I can't take a real order yet — but I can show you what's good today.",
};

/**
 * Stubbed "Ask our barista bot" chat panel.
 *
 * This is a DEMO placeholder — there is no real LLM and nothing is ordered. It
 * does, however, stay grounded: its canned replies read real item names + prices
 * from the twin, never invented ones. Mounted only while open (see App), so it
 * resets fresh each time. Accessibility: focus-managed modal dialog (Esc closes,
 * focus returns to the opener on close, backdrop click closes).
 */
export function BaristaBotPanel({ onClose, featured }: Props) {
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 50);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  const popular = featured
    .slice(0, 3)
    .map((i) => `${i.name} (${formatPrice(i.price_cents)})`)
    .join(', ');

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    setMessages((prev) => [
      ...prev,
      { from: 'you', text },
      {
        from: 'bot',
        text: `Thanks! In the full version I'd build that order for you. For now, a few favorites from today's live menu: ${popular}. Tap "Order Now" to browse the menu.`,
      },
    ]);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
      role="presentation"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close chat"
        onClick={onClose}
        className="absolute inset-0 bg-espresso/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bot-title"
        className="relative flex h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-t-[var(--radius-card)] bg-cream-50 shadow-2xl sm:h-[70vh] sm:rounded-[var(--radius-card)]"
      >
        <header className="flex items-center justify-between gap-3 border-b border-mocha/15 bg-espresso px-4 py-3 text-cream-50">
          <div>
            <h2 id="bot-title" className="text-lg font-bold">
              Barista bot
            </h2>
            <p className="text-xs text-foam">Demo · no real orders placed</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-cream-50/10 px-3 py-1.5 text-sm font-semibold hover:bg-cream-50/20"
          >
            Close
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.from === 'you' ? 'justify-end' : 'justify-start'}`}
            >
              <p
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                  m.from === 'you'
                    ? 'bg-accent text-cream-50'
                    : 'bg-cream-200 text-espresso'
                }`}
              >
                <span className="sr-only">
                  {m.from === 'you' ? 'You said: ' : 'Barista bot said: '}
                </span>
                {m.text}
              </p>
            </div>
          ))}
        </div>

        <form
          onSubmit={handleSend}
          className="flex items-center gap-2 border-t border-mocha/15 p-3"
        >
          <label htmlFor="bot-input" className="sr-only">
            Message the barista bot
          </label>
          <input
            id="bot-input"
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Try: a large oat latte…"
            className="min-h-[44px] flex-1 rounded-full border border-mocha/25 bg-white px-4 text-espresso placeholder:text-latte"
          />
          <button
            type="submit"
            className="min-h-[44px] rounded-full bg-accent px-5 font-semibold text-cream-50 hover:bg-accent-dark"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
